import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';

const FoodCalorieInsights = () => {
  const [image, setImage] = useState(null);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editableResponse, setEditableResponse] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const genAI = new GoogleGenerativeAI('AIzaSyDQOI2wVuOqxtlZ3j_888NhUM6Tu5kRFrk');

  const acceptedImageFormats = 'image/png, image/jpeg, image/jpg, image/gif, image/webp';

  useEffect(() => {
    if (showCamera) {
      startCamera();
    } else {
      stopCamera();
    }
  }, [showCamera]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (acceptedImageFormats.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => setImage(e.target.result);
        reader.readAsDataURL(file);
      } else {
        alert('Please upload an image in PNG, JPEG, JPG, GIF, or WebP format.');
      }
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setShowCamera(true);
    } catch (err) {
      console.error("Error accessing the camera:", err);
      let errorMessage = "Unable to access the camera. ";
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage += "Please grant camera permissions in your browser settings.";
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage += "No camera found on your device.";
      } else if (err.name === 'NotSupportedError') {
        errorMessage += "Your browser doesn't support camera access.";
      } else if (err.name === 'InsecureContextError') {
        errorMessage += "Camera access requires a secure connection (HTTPS).";
      } else {
        errorMessage += "Please check your camera and try again.";
      }
      setCameraError(errorMessage);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setShowCamera(false);
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageDataUrl = canvas.toDataURL('image/jpeg');
      setImage(imageDataUrl);
      setShowCamera(false);
    }
  };

  const cleanAndParseJSON = (text) => {
    try {
      // Remove markdown formatting if present
      const cleanedText = text.replace(/```json\n?|\n?```/g, '').trim();
      return JSON.parse(cleanedText);
    } catch (error) {
      console.error('JSON parsing failed:', error);
      return { error: "Failed to parse the response. Please try again." };
    }
  };

  const getGeminiResponse = async () => {
    if (!image) {
      alert('Please upload or capture an image first.');
      return;
    }
    setLoading(true);
    try {
      const model = genAI.getGenerativeModel({ model: 'gemini-2.5-pro' });
      const prompt = `
        You are a nutrition expert AI. Analyze the provided image and follow these steps:

        1. Determine if the image contains food. If it does not contain food, respond with only this JSON:
           { "error": "This image does not contain food. Please upload an image of food for analysis." }

        2. If the image contains food, provide the following information in a structured format:
           a. Dish name (if it's a prepared dish, otherwise use "Various food items")
           b. List of identifiable food items with the following details for each:
              - Estimated quantity (be specific, e.g., "1 slice", "100g", "1 cup")
              - Approximate calories
              - Estimated protein content (in grams)
              - Estimated carbohydrate content (in grams)
              - Estimated fat content (in grams)
              - Estimated fiber content (in grams)
              - List of healthy ingredients or nutrients
              - List of potentially unhealthy ingredients or concerns
              - Health score (1-10, where 10 is extremely healthy)
           c. Total estimated calories for all items
           d. Total estimated protein, carbs, and fats
           e. Overall health score (1-10) for the entire meal or collection of food items

        3. Format the response as a JSON object with the following structure:
        {
          "dishName": "string",
          "foodItems": [
            {
              "name": "string",
              "quantity": "string",
              "calories": number,
              "protein": number,
              "carbs": number,
              "fat": number,
              "fiber": number,
              "healthyIngredients": ["string"],
              "unhealthyIngredients": ["string"],
              "healthScore": number
            }
          ],
          "totalCalories": number,
          "totalProtein": number,
          "totalCarbs": number,
          "totalFat": number,
          "overallHealthScore": number
        }

        Ensure all numeric values are numbers, not strings. If you cannot determine a value, use null instead of a placeholder number.
      `;

      const result = await model.generateContent({
        contents: [{ role: 'user', parts: [{ text: prompt }, { inlineData: { data: image.split(',')[1], mimeType: 'image/jpeg' } }]}],
        generationConfig: { temperature: 0.1 }
      });

      const responseText = result.response.text();
      console.log('Raw API response:', responseText);
      const parsedResponse = cleanAndParseJSON(responseText);
      setResponse(parsedResponse);
      setEditableResponse(JSON.parse(JSON.stringify(parsedResponse)));
    } catch (error) {
      console.error('Error:', error);
      setResponse({ error: 'An error occurred while processing your request. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = useCallback((index, field, value) => {
    setEditableResponse(prevState => {
      const updatedFoodItems = [...prevState.foodItems];
      let item = { ...updatedFoodItems[index] };

      if (field === 'quantity') {
        const newQuantity = parseFloat(value) || 0;
        const oldQuantity = parseFloat(item.quantity) || 1;
        const ratio = newQuantity / oldQuantity;
        
        item = {
          ...item,
          quantity: newQuantity,
          calories: Math.round((item.calories || 0) * ratio),
          protein: Math.round(((item.protein || 0) * ratio) * 10) / 10,
          carbs: Math.round(((item.carbs || 0) * ratio) * 10) / 10,
          fat: Math.round(((item.fat || 0) * ratio) * 10) / 10,
          fiber: Math.round(((item.fiber || 0) * ratio) * 10) / 10
        };
      } else {
        item = { ...item, [field]: value };
      }

      updatedFoodItems[index] = item;

      const totalCalories = updatedFoodItems.reduce((sum, item) => sum + (item.calories || 0), 0);
      const totalProtein = updatedFoodItems.reduce((sum, item) => sum + (item.protein || 0), 0);
      const totalCarbs = updatedFoodItems.reduce((sum, item) => sum + (item.carbs || 0), 0);
      const totalFat = updatedFoodItems.reduce((sum, item) => sum + (item.fat || 0), 0);

      return {
        ...prevState,
        foodItems: updatedFoodItems,
        totalCalories: Math.round(totalCalories),
        totalProtein: Math.round(totalProtein * 10) / 10,
        totalCarbs: Math.round(totalCarbs * 10) / 10,
        totalFat: Math.round(totalFat * 10) / 10
      };
    });
  }, []);

  const handleSaveEdits = useCallback(() => {
    if (editableResponse) {
      setResponse(editableResponse);
      setShowEditModal(false);
    }
  }, [editableResponse]);

  const getHealthScoreBadge = (score) => {
    if (score >= 8) return <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">Healthy</span>;
    if (score >= 5) return <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded">Moderate</span>;
    return <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">Unhealthy</span>;
  };

  const EditableItem = useCallback(({ item, index, onEdit }) => (
    <div className="bg-white shadow rounded-lg p-4 mb-4">
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700">Food Item Name</label>
        <input
          type="text"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          value={item.name}
          onChange={(e) => onEdit(index, 'name', e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-4 mb-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Quantity</label>
          <input
            type="number"
            step="0.1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            value={item.quantity}
            onChange={(e) => onEdit(index, 'quantity', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Calories</label>
          <input
            type="number"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            value={item.calories}
            onChange={(e) => onEdit(index, 'calories', parseInt(e.target.value) || 0)}
          />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4 mb-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Protein (g)</label>
          <input
            type="number"
            step="0.1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            value={item.protein}
            onChange={(e) => onEdit(index, 'protein', parseFloat(e.target.value) || 0)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Carbs (g)</label>
          <input
            type="number"
            step="0.1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            value={item.carbs}
            onChange={(e) => onEdit(index, 'carbs', parseFloat(e.target.value) || 0)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Fat (g)</label>
          <input
            type="number"
            step="0.1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            value={item.fat}
            onChange={(e) => onEdit(index, 'fat', parseFloat(e.target.value) || 0)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Fiber (g)</label>
          <input
            type="number"
            step="0.1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            value={item.fiber}
            onChange={(e) => onEdit(index, 'fiber', parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700">Healthy Ingredients</label>
        <textarea
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          value={item.healthyIngredients.join(", ")}
          onChange={(e) => onEdit(index, 'healthyIngredients', e.target.value.split(", "))}
        />
      </div>
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700">Unhealthy Ingredients</label>
        <textarea
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          value={item.unhealthyIngredients.join(", ")}
          onChange={(e) => onEdit(index, 'unhealthyIngredients', e.target.value.split(", "))}
        />
      </div>
    </div>
  ), []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-indigo-200 flex flex-col items-center justify-center p-4 md:p-8">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-4xl">
        <h1 className="text-4xl font-extrabold text-center mb-8 text-indigo-700">Know Your Food Better</h1>

        <p className="mb-6 text-center text-gray-600">Upload an image or use the camera to analyze food:</p>
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <label className="flex-1 flex flex-col items-center px-4 py-6 bg-white text-indigo-500 rounded-lg shadow-md border-2 border-indigo-300 cursor-pointer hover:bg-indigo-50 transition duration-300 ease-in-out">
            <svg className="w-8 h-8" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c0 .38-.04.74-.12 1.1zM11 11h3l-4-4-4 4h3v3h2v-3z" />
            </svg>
            <span className="mt-2 text-base leading-normal">Select a file</span>
            <input
              type='file'
              className="hidden"
              onChange={handleImageUpload}
              accept={acceptedImageFormats}
              ref={fileInputRef}
            />
          </label>
          <button
            onClick={() => setShowCamera(!showCamera)}
            className="flex-1 px-6 py-3 font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition duration-300 ease-in-out"
          >
            {showCamera ? 'Close Camera' : 'Open Camera'}
          </button>
        </div>

        {showCamera && (
          <div className="mb-6">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-64 object-cover rounded-lg shadow-md"
            />
            <button
              onClick={captureImage}
              className="mt-4 w-full px-6 py-3 font-semibold text-white bg-green-500 rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-300 ease-in-out"
            >
              Capture Image
            </button>
          </div>
        )}

        {cameraError && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md mb-6" role="alert">
            <p className="font-bold">Error</p>
            <p>{cameraError}</p>
          </div>
        )}

        {image && (
          <div className="mb-6">
            <img src={image} alt="Uploaded or captured food" className="w-full h-auto rounded-lg shadow-md" />
          </div>
        )}

        <button
          onClick={getGeminiResponse}
          disabled={loading || !image}
          className={`w-full px-6 py-3 font-semibold text-white bg-indigo-600 rounded-lg ${loading || !image ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition duration-300 ease-in-out`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : 'Analyze Food'}
        </button>

        <canvas ref={canvasRef} className="hidden" />

        {response && !response.error && (
          <div className="mt-8 bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="bg-indigo-600 text-white px-6 py-4">
              <h2 className="text-2xl font-bold">Food Calorie Insights</h2>
            </div>
            <div className="p-6">
            <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-700 p-4 mb-6" role="alert">
                <p className="font-bold">Disclaimer:</p>
                <p>The information provided is an estimate based on AI analysis. For accurate nutritional information, please consult product labels or a nutritionist.</p>
              </div>
              <h3 className="text-xl font-semibold mb-4">{response.dishName} {getHealthScoreBadge(response.overallHealthScore)}</h3>

              <div className="overflow-x-auto shadow-md rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {["Food Item", "Quantity", "Calories", "Protein", "Carbs", "Fat", "Fiber", "Healthy Ingredients", "Unhealthy Ingredients", "Health Score"].map((header) => (
                        <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {response.foodItems.map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="px-6 py-4 whitespace-nowrap">{item.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.quantity || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.calories ?? 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.protein ? `${item.protein}g` : 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.carbs ? `${item.carbs}g` : 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.fat ? `${item.fat}g` : 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.fiber ? `${item.fiber}g` : 'N/A'}</td>
                        <td className="px-6 py-4">{item.healthyIngredients?.join(", ") || 'N/A'}</td>
                        <td className="px-6 py-4">{item.unhealthyIngredients?.join(", ") || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.healthScore ? getHealthScoreBadge(item.healthScore) : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="bg-gray-100 p-4 rounded-lg"><strong>Total Calories:</strong> {response.totalCalories ?? 'N/A'}</div>
                <div className="bg-gray-100 p-4 rounded-lg"><strong>Total Protein:</strong> {response.totalProtein ? `${response.totalProtein}g` : 'N/A'}</div>
                <div className="bg-gray-100 p-4 rounded-lg"><strong>Total Carbs:</strong> {response.totalCarbs ? `${response.totalCarbs}g` : 'N/A'}</div>
                <div className="bg-gray-100 p-4 rounded-lg"><strong>Total Fat:</strong> {response.totalFat ? `${response.totalFat}g` : 'N/A'}</div>
              </div>
              <div className="mt-6 bg-gray-100 p-4 rounded-lg">
                <strong>Overall Health Score:</strong> {response.overallHealthScore ? `${response.overallHealthScore}/10` : 'N/A'}
                {response.overallHealthScore && getHealthScoreBadge(response.overallHealthScore)}
              </div>
              <div className="mt-4 bg-gray-100 p-4 rounded-lg">
                <strong>Percentage of Daily Calorie Intake:</strong>
                {response.totalCalories ? `${Math.round((response.totalCalories / 2000) * 100)}%` : 'N/A'}
              </div>
              <button
                onClick={() => setShowEditModal(true)}
                className="mt-6 px-6 py-3 font-semibold text-white bg-green-500 rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-300 ease-in-out"
              >
                Edit Information
              </button>
            </div>
          </div>
        )}

        {response && response.error && (
          <div className="mt-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md" role="alert">
            <p className="font-bold">Error</p>
            <p>{response.error}</p>
          </div>
        )}

        {showEditModal && (
          <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                    Edit Food Information
                  </h3>
                  {editableResponse && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Dish Name</label>
                        <input
                          type="text"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          value={editableResponse.dishName}
                          onChange={(e) => setEditableResponse(prev => ({ ...prev, dishName: e.target.value }))}
                        />
                      </div>
                      {editableResponse.foodItems.map((item, index) => (
                        <EditableItem key={index} item={item} index={index} onEdit={handleEdit} />
                      ))}
                    </>
                  )}
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleSaveEdits}
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FoodCalorieInsights;
